import { Link } from '@tanstack/react-router';

import { Button } from '@/components/button';
import {
  DisplayHeading,
  Eyebrow,
  IndexNumber,
  Tag,
} from '@/components/editorial';
import { servicesData } from '@/lib/data';

export function ServicesPageContent() {
  return (
    <div className="container max-w-5xl px-6 py-16 md:py-24">
      <div className="mb-14 max-w-2xl">
        <Eyebrow className="mb-4">What I offer</Eyebrow>
        <DisplayHeading as="h1" size="xl" animate={false}>
          Services
        </DisplayHeading>
        <p className="text-muted-foreground mt-4 text-lg leading-relaxed">
          Targeting Singapore remote full time. Also open to contracts,
          freelance, and part time work when the impact is clear. Based in
          Jakarta, GMT+7, same working day as Singapore.
        </p>
        <div className="mt-6">
          <Button variant="brand" size="pill" asChild>
            <Link to="/contact">Get in touch</Link>
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-10">
        {servicesData.map((service, index) => (
          <article
            key={service.id}
            id={`service-${service.id}`}
            className="border-border scroll-mt-28 rounded-2xl border p-6 md:p-8"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <IndexNumber value={index + 1} />
              <p className="text-muted-foreground font-mono text-xs">
                {service.rate}
              </p>
            </div>

            <h2 className="font-display text-ink mt-4 text-2xl font-semibold md:text-3xl">
              {service.title}
            </h2>
            <p className="text-muted-foreground mt-3 max-w-2xl text-base leading-relaxed">
              {service.description}
            </p>

            <div className="mt-8 grid gap-8 md:grid-cols-2">
              <div>
                <p className="text-muted-foreground mb-3 font-mono text-[10px] uppercase tracking-widest">
                  What you get
                </p>
                <ul className="space-y-2">
                  {service.deliverables.map((item) => (
                    <li
                      key={item}
                      className="text-ink flex gap-2.5 text-sm leading-relaxed"
                    >
                      <span
                        aria-hidden
                        className="bg-brand/70 mt-2 size-1.5 shrink-0 rounded-full"
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-muted-foreground mb-3 font-mono text-[10px] uppercase tracking-widest">
                  Good fit when
                </p>
                <ul className="space-y-2">
                  {service.fitFor.map((item) => (
                    <li
                      key={item}
                      className="text-ink flex gap-2.5 text-sm leading-relaxed"
                    >
                      <span
                        aria-hidden
                        className="bg-brand/70 mt-2 size-1.5 shrink-0 rounded-full"
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {service.outcome && (
              <p className="border-border text-muted-foreground mt-8 border-t pt-6 text-sm leading-relaxed">
                <span className="text-ink font-medium">Outcome signal: </span>
                {service.outcome}
              </p>
            )}

            <div className="mt-6 flex flex-wrap gap-1.5">
              {service.stack.map((tech) => (
                <Tag key={tech} variant="muted">
                  {tech}
                </Tag>
              ))}
            </div>
          </article>
        ))}
      </div>

      <section className="border-border bg-muted/30 mt-16 rounded-2xl border p-6 md:p-10">
        <Eyebrow className="mb-3">How I work</Eyebrow>
        <h2 className="font-display text-ink text-2xl font-semibold">
          Visual, async-friendly, outcome-led
        </h2>
        <ul className="text-muted-foreground mt-4 max-w-2xl space-y-2 text-sm leading-relaxed">
          <li>
            · I sketch systems first with diagrams and Figma, so the team shares
            one picture before code lands.
          </li>
          <li>
            · I write decisions down. Remote and hybrid teams move faster with
            clear written context.
          </li>
          <li>
            · Primary goal is Singapore remote full time; selective freelance
            and contracts welcome when the problem is worth solving.
          </li>
        </ul>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button variant="brand" size="pill" asChild>
            <Link to="/contact">Start a conversation</Link>
          </Button>
          <Button variant="outline-brand" size="pill" asChild>
            <Link to="/projects">See projects</Link>
          </Button>
        </div>
      </section>

      <div className="mt-12">
        <Link
          to="/"
          className="text-muted-foreground hover:text-brand font-mono text-sm transition-colors"
        >
          ← Back to home
        </Link>
      </div>
    </div>
  );
}
